import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useFinance } from '@/contexts/FinanceContext';
import type { CounterpartyKind, FinanceCounterparty } from '@/types/finance';

interface Props {
  value?: string | undefined;
  onChange: (id: string | undefined) => void;
  /**
   * Filtra a lista por papel: `expense` mostra fornecedores e ambos;
   * `income` mostra clientes e ambos. `any` mostra todos (default).
   */
  role?: 'expense' | 'income' | 'any';
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowClear?: boolean;
}

function matchesRole(cp: FinanceCounterparty, role: Props['role']): boolean {
  if (!role || role === 'any') return true;
  if (cp.kind === 'both') return true;
  if (role === 'expense') return cp.kind === 'supplier';
  return cp.kind === 'customer';
}

export function CounterpartyCombobox({
  value,
  onChange,
  role = 'any',
  placeholder = 'Selecionar contraparte…',
  disabled,
  className,
  allowClear = true,
}: Props) {
  const { counterparties, addCounterparty } = useFinance();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const visibleAll = useMemo(
    () => counterparties.filter((cp) => matchesRole(cp, role)),
    [counterparties, role],
  );

  const trimmed = query.trim();
  const lower = trimmed.toLowerCase();

  const filtered = useMemo(() => {
    if (!lower) return visibleAll;
    return visibleAll.filter((cp) => cp.name.toLowerCase().includes(lower));
  }, [visibleAll, lower]);

  const exactMatch = useMemo(
    () => visibleAll.find((cp) => cp.name.toLowerCase() === lower),
    [visibleAll, lower],
  );

  const selected = value ? counterparties.find((cp) => cp.id === value) : undefined;

  const inferKind = (): CounterpartyKind => {
    if (role === 'expense') return 'supplier';
    if (role === 'income') return 'customer';
    return 'both';
  };

  const handleCreate = async () => {
    if (!trimmed || creating) return;
    setCreating(true);
    try {
      const created = await addCounterparty({ name: trimmed, kind: inferKind() });
      onChange(created.id);
      setOpen(false);
      setQuery('');
    } finally {
      setCreating(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">{selected ? selected.name : placeholder}</span>
          <span className="ml-2 flex flex-shrink-0 items-center gap-1">
            {selected && allowClear && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                className="rounded p-0.5 text-muted-foreground hover:bg-secondary"
                aria-label="Remover seleção"
              >
                <X size={12} />
              </span>
            )}
            <ChevronsUpDown size={14} className="opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar ou digitar para criar…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              {trimmed ? (
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-secondary"
                >
                  <Plus size={14} />
                  Criar &quot;{trimmed}&quot;
                </button>
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  Nenhuma contraparte cadastrada.
                </div>
              )}
            </CommandEmpty>
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((cp) => (
                  <CommandItem
                    key={cp.id}
                    value={cp.name}
                    onSelect={() => {
                      onChange(cp.id);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    <Check
                      size={14}
                      className={cn('mr-2', value === cp.id ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="flex-1 truncate">{cp.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {trimmed && !exactMatch && filtered.length > 0 && (
              <CommandGroup>
                <CommandItem
                  value={`__create__:${trimmed}`}
                  onSelect={handleCreate}
                  disabled={creating}
                  className="text-primary"
                >
                  <Plus size={14} className="mr-2" />
                  Criar &quot;{trimmed}&quot;
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
